import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import { cn } from '@crossnokaye/ux-common';

import { trimBackground } from '@/features/equipment-browser/sequencer-section/compressor-sequencer/helpers';

import type { EnergyAIStageRow } from '../compressor-energy-ai.types';

/** Minimum visible fill, so a ramp that has barely started still reads as a bar. */
const MINIMUM_FILL_PERCENT = 8;

interface CompressorEnergyAIRampSliderProps {
  row: EnergyAIStageRow;
}

export const CompressorEnergyAIRampSlider = ({ row }: CompressorEnergyAIRampSliderProps) => {
  if (row.progress === undefined || row.direction === 'none') {
    return null;
  }

  const isRampingDown = row.direction === 'down';
  const fillPercent = isRampingDown ? (1 - row.progress) * 100 : row.progress * 100;
  const widthPercent = Math.max(MINIMUM_FILL_PERCENT, Math.round(fillPercent));
  const ChevronIcon = isRampingDown ? ChevronLeftIcon : ChevronRightIcon;
  const isTrim = row.state === 'trim';

  // Decorative: the ramp direction and percentage are already announced as text
  // by the containing cell, so a second progressbar node would read the same
  // value twice in every ramping row. The chevron sits on the fill boundary and
  // nudges at a fixed speed, so a long ramp still reads as moving while the bar
  // itself barely changes.
  return (
    <div
      aria-hidden="true"
      className="absolute inset-1 overflow-hidden border border-white/20 bg-ckds-neutral-900"
      data-component="CompressorEnergyAIRampSlider"
    >
      <div
        className={cn('absolute inset-y-0 left-0', !isTrim && 'bg-ckds-brand-500')}
        style={{
          ...(isTrim ? { background: trimBackground } : {}),
          width: `${widthPercent}%`,
        }}
      />
      <ChevronIcon
        className={cn(
          'absolute top-1/2 h-4 w-4 stroke-conserving stroke-[2.5]',
          isRampingDown
            ? '-translate-y-1/2 motion-safe:animate-ramp-nudge-left'
            : '-translate-x-full -translate-y-1/2 motion-safe:animate-ramp-nudge-right'
        )}
        style={{ left: `${widthPercent}%` }}
      />
    </div>
  );
};
